import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map, switchMap, take, timeout } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

export const superAdminGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Check if user is authenticated
  if (!authService.isLoggedIn()) {
    router.navigate(['/login']);
    return false;
  }

  // Get current roles, and if empty, trigger user data loading
  const currentRoles = authService.getCurrentUserRoles();

  if (currentRoles.length === 0) {
    // Trigger user data loading and wait for roles
    return authService.getCurrentUser().pipe(
      // Switch to the roles observable after loading user data
      switchMap(() => authService.userRoles$),
      // Wait up to 10 seconds for roles to load
      timeout(10000),
      // Take the first emission with roles
      take(1),
      // Check Super Admin access based on permission
      map(() => checkFeatureFlagAccess(authService, router)),
      // Handle errors
      catchError((error) => {
        console.error('Error loading user roles in Super Admin guard:', error);
        router.navigate(['/admin/users']);
        return of(false);
      })
    );
  }

  // Roles already loaded, check immediately
  return checkFeatureFlagAccess(authService, router);
};

function checkFeatureFlagAccess(
  authService: AuthService,
  router: Router
): boolean {
  // Check if user has the specific permission to view feature flag management
  const hasAccess = authService.canViewFeatureFlagManagement();

  if (!hasAccess) {
    console.warn(
      'Access denied: User does not have permission to view feature flag management'
    );
    router.navigate(['/admin/users']);
    return false;
  }

  return true;
}
